import { useDispatch } from 'react-redux'

import { AppVersionReducer } from '@AppState/app-version'
import { TicketModeReducer } from '@AppState/ticket-info'
import { AsyncThunkAction, configureStore, Dispatch } from '@reduxjs/toolkit'

import { AnalyticsModeReducer } from './analytics-mode/analytics-mode-slice'
import { AnimationReducer } from './animation'
import { AppConfigReducer } from './app-config'
import { AppSettingsReducer } from './app-settings'
import { GameConfigReducer } from './game-config'
import { HostConfigReducer } from './host-config'
import { LatestDrawReducer } from './latest-draw'
import { NavigationReducer } from './navigation'
import { PollingActions, PollingReducer } from './polling/polling-slice'
import { TicketHistoryModeReducer } from './ticket-history'
import { ViewModeReducer } from './view-mode'

export const STORE_REDUCER = {
  analyticsMode: AnalyticsModeReducer,
  animation: AnimationReducer,
  appConfig: AppConfigReducer,
  appSettings: AppSettingsReducer,
  appVersion: AppVersionReducer,
  hostConfig: HostConfigReducer,
  latestDraw: LatestDrawReducer,
  gameConfig: GameConfigReducer,
  navigation: NavigationReducer,
  polling: PollingReducer,
  ticketMode: TicketModeReducer,
  ticketHistoryMode: TicketHistoryModeReducer,
  viewMode: ViewModeReducer,
}

export const store = configureStore({
  reducer: STORE_REDUCER,
})

export const pollingRequest =
  <T>(
    thunkAction: AsyncThunkAction<unknown, T, { dispatch: Dispatch }>,
    pollingId: string,
    interval: number = 60000,
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    let intervalId = getState()?.polling?.intervalMap[pollingId]
    // Fetch first and then start polling
    dispatch(thunkAction)

    if (intervalId) {
      console.warn('polling is already set up for this action')
    } else {
      console.log('starting polling listener')
      intervalId = setInterval(() => {
        dispatch(thunkAction)
      }, interval)
      dispatch(PollingActions.setPollingInterval({ thunkId: pollingId, intervalId }))
    }

    // Return a cleanup function to stop polling
    return () => {
      console.log('cleaning up interval')
      dispatch(PollingActions.clearPollingInterval(pollingId))
    }
  }

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch

export const useAppDispatch = () => useDispatch<typeof store.dispatch>()
